/**
 * Fixed raster settings for figure extraction.
 *
 * The playground tuned these values; they are frozen here so the admin
 * playground and the production import pipeline rasterise a figure the same
 * way. Change a value here and both paths follow. Do not fork these numbers.
 *
 * @format
 */

import type { ClusteredFigure, FigureBox } from "./extract-figure-svg.ts";

/** Settings for the trimmed backup raster of one figure. */
export interface FigureRasterSettings {
	/** Render resolution of the page, as a multiplier of the PDF point grid. */
	scale: number;
	/** Percent (0-100) of the horizontal gap to the content frame to fill per side. */
	padPctX: number;
	/** Percent (0-100) of the vertical gap to the content frame to fill per side. */
	padPctY: number;
	/** Breathing margin around the trimmed content, in points. */
	trimMargin: number;
	/** Maximum distance a word box may pull the trim from the figure, in points. */
	trimReach: number;
}

/**
 * The frozen values the playground settled on. The trimmed raster crops a
 * padded region to the figure and the words drawn on its SVG, so it drops white
 * space and unrelated items while keeping the figure's own labels.
 */
export const FIGURE_RASTER_SETTINGS: FigureRasterSettings = {
	scale: 3,
	padPctX: 100,
	padPctY: 50,
	trimMargin: 6,
	trimReach: 70,
};

/** One figure region to rasterise, carrying the fixed settings. */
export interface FigureRasterRegion {
	page: number;
	box: FigureBox;
	padPctX: number;
	padPctY: number;
	wordBoxes: FigureBox[];
	trimMargin: number;
	trimReach: number;
}

/**
 * Build the backup-raster region for one figure. Both the admin playground and
 * the production import pipeline call this, so the region shape — the box and
 * the word set the trim follows — is decided in one place and can not drift
 * between the two paths.
 *
 * The trim follows the passed word set, which must be every offered candidate,
 * not the subset a model drew, so the backup never loses a label the model
 * dropped. Pass the word boxes from {@link wordingCandidates} as
 * `{ x, y: run.y, width, height }` (the run's baseline origin).
 *
 * The settings are a parameter, not fixed inside, because the playground tunes
 * them before they are frozen. Production passes {@link FIGURE_RASTER_SETTINGS};
 * the playground passes its current knob values. The logic stays shared; only
 * the numbers differ while tuning, and a frozen value flows to both.
 */
export const figureRasterRegion = (
	figure: ClusteredFigure,
	candidateBoxes: readonly FigureBox[],
	settings: FigureRasterSettings = FIGURE_RASTER_SETTINGS,
): FigureRasterRegion => ({
	page: figure.page,
	box: { x: figure.x, y: figure.y, width: figure.width, height: figure.height },
	padPctX: settings.padPctX,
	padPctY: settings.padPctY,
	wordBoxes: candidateBoxes.map((box) => ({ x: box.x, y: box.y, width: box.width, height: box.height })),
	trimMargin: settings.trimMargin,
	trimReach: settings.trimReach,
});
